import { useState, useMemo, useCallback, useEffect } from 'react';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { QUERY_GET_COUNTRIES } from 'common/queries';
import { setItem } from '~helpers/storage';
import { mergeSearchParams } from '~helpers/history';
import {
  landlordSortBy,
  landlordStatus,
  LANDLORD_STATUS_ALL,
  LANDLORD_STATUS_PENDING,
} from '~constants/landlord';
import { QUERY_LANDLORDS, QUERY_LANDLORDS_PENDING_COUNT } from './queries';

const formatQueryParam = query => {
  if (!query) {
    return [];
  }
  if (typeof query === 'string') {
    return [query];
  }
  if (Array.isArray(query)) {
    return query;
  }

  return [];
};

export const PENDING_TAB = 0;
export const ALL_TAB = 1;

export const DISPLAY_ALL_FILTER = '1';
export const OWNED_BY_ME_FILTER = '2';

const defaultFilters = {
  pageSize: 10,
  pageNumber: 1,
  sortBy: undefined,
  sortDirection: undefined,
};

const useLandlords = () => {
  const history = useHistory();
  const location = useLocation();

  const { email: ownedByMeEmail } =
    useSelector(state => state.dashboard.account?.toJS()?.account) ?? {};

  const queryParams = queryString.parse(location.search);

  const [currentTab, setCurrentTab] = useState(() => {
    if (queryParams.statuses === LANDLORD_STATUS_PENDING) {
      return PENDING_TAB;
    } else {
      return ALL_TAB;
    }
  });

  const [filters, setFilters] = useState(() => {
    let statuses = queryParams.statuses;

    if (!statuses || statuses === LANDLORD_STATUS_ALL) {
      statuses = '';
    } else if (statuses === LANDLORD_STATUS_PENDING) {
      statuses = landlordStatus.PENDING;
    }

    return {
      pageNumber: queryParams.pageNumber
        ? parseInt(queryParams.pageNumber, 10)
        : defaultFilters.pageNumber,
      pageSize: queryParams.pageSize ? parseInt(queryParams.pageSize, 10) : defaultFilters.pageSize,
      statuses: formatQueryParam(statuses),
      billingCountryIds: formatQueryParam(queryParams.billingCountryIds),
      accountManager: queryParams.accountManager,
      slugs: undefined,
      registerTypes: undefined,
      sortBy: undefined,
      sortDirection: undefined,
    };
  });

  const [searchParams, setSearchParams] = useState({});

  const [pendingPageNumber, setPendingPageNumber] = useState(1);
  const [pendingTotal, setPendingTotal] = useState(0);

  const [queryLandlords, { called, data: landlordData, loading, fetchMore, refetch }] =
    useLazyQuery(QUERY_LANDLORDS, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      onCompleted: res => {
        if (currentTab === PENDING_TAB) {
          setPendingTotal(res.landlords.totalCount);
        }
      },
    });

  const { data: pendingCountData } = useQuery(QUERY_LANDLORDS_PENDING_COUNT, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    let accountManager = filters.accountManager;
    let billingCountryIds = filters.billingCountryIds;

    if (accountManager === OWNED_BY_ME_FILTER) {
      accountManager = ownedByMeEmail;
    } else {
      accountManager = undefined;
    }

    setPendingPageNumber(1);

    queryLandlords({
      variables: {
        ...filters,
        accountManager,
        billingCountryIds,
        sortBy:
          currentTab === PENDING_TAB
            ? landlordSortBy.PENDING_AT
            : filters.sortBy
            ? filters.sortBy
            : undefined,
      },
    });
  }, [currentTab, filters, ownedByMeEmail, queryLandlords]);

  const landlords = useMemo(() => {
    const { edges, totalCount = 0, pageInfo } = landlordData?.landlords ?? {};
    let dataSource = [];

    if (edges?.length) {
      dataSource = edges.map(item => item.node);
    }

    return {
      dataSource,
      total: totalCount,
      pageInfo,
    };
  }, [landlordData]);

  const handleChangeFilters = useCallback(
    (params, from = 'filter') => {
      if (
        Object.keys(params).some(key =>
          ['pageNumber', 'pageSize', 'billingCountryIds', 'accountManager'].includes(key),
        ) ||
        (from === 'tab' && params.statuses)
      ) {
        const {
          statuses = filters.statuses,
          billingCountryIds,
          accountManager,
          pageNumber = filters.pageNumber,
          pageSize = filters.pageSize,
        } = params;

        const tempSearchParams = {
          ...searchParams,
          pageNumber,
          pageSize,
          statuses,
        };

        if (billingCountryIds !== undefined) {
          tempSearchParams.billingCountryIds = billingCountryIds;
        }

        if (accountManager !== undefined) {
          tempSearchParams.accountManager = accountManager;
        }

        if (!statuses?.[0]) {
          tempSearchParams.statuses = [LANDLORD_STATUS_ALL];
        }

        setSearchParams(tempSearchParams);

        history.push(mergeSearchParams(tempSearchParams));

        setItem(
          'cms_landlords_list_filters',
          queryString.parse(mergeSearchParams(tempSearchParams)),
        );
      }

      ['registerTypes', 'slugs'].forEach(item => {
        if (params[item] && !params[item]?.length) {
          params[item] = undefined;
        }
      });

      setFilters(prevFilter => ({
        ...prevFilter,
        ...params,
      }));
    },
    [filters, history, searchParams],
  );

  const handleToggleTab = useCallback(
    tab => {
      let tabStatuses = [landlordStatus.PENDING];

      if (tab === ALL_TAB) {
        tabStatuses = [];
      } else {
        tabStatuses = [landlordStatus.PENDING];
      }

      setCurrentTab(tab);

      handleChangeFilters(
        {
          statuses: tabStatuses,
          slugs: [],
          registerTypes: [],
          ...defaultFilters,
        },
        'filter',
      );
    },
    [handleChangeFilters],
  );

  useEffect(() => {
    if (pendingCountData?.landlords?.totalCount !== undefined) {
      setPendingTotal(pendingCountData.landlords.totalCount);
    }
  }, [pendingCountData]);

  return {
    currentTab,
    filters,
    setFilters,
    landlords,
    queryLandlordsLoading: loading,
    queryLandlordsCalled: called,
    fetchMoreLandlords: fetchMore,
    refetchLandlords: refetch,
    handleToggleTab,
    handleChangeFilters,
    pendingPageNumber,
    setPendingPageNumber,
    pendingTotal,
  };
};

export const useGetCountries = () => {
  const { loading, data } = useQuery(QUERY_GET_COUNTRIES, {
    variables: {
      pageSize: 50,
    },
  });
  return {
    countriesData: data?.countries?.edges?.map(edge => ({
      value: edge?.node?.id,
      text: edge?.node?.name,
    })),
    loading: loading,
  };
};

export default useLandlords;
